import type { ReactNode } from 'react';

// Form primitives, editorial-styled: hairline boxes, mono labels, near-square
// corners. Selection is shown by inverting to ink — no accent colour, because
// vermilion means "act on this", not "you picked this".

export function Field({
  index,
  label,
  hint,
  children,
}: {
  index: number;
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="border-t border-rule pt-4">
      <legend className="sr-only">{label}</legend>
      <div className="flex items-baseline gap-2">
        <span className="kicker tabular">{String(index).padStart(2, '0')}</span>
        <span className="display-sm text-lg">{label}</span>
      </div>
      {hint && <p className="mt-1 text-sm text-ink-faint">{hint}</p>}
      <div className="mt-3">{children}</div>
    </fieldset>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((option) => {
        const selected = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.id)}
            className={`rounded-xs border px-3 py-2 text-left text-sm transition-colors ${
              selected
                ? 'border-ink bg-ink text-paper'
                : 'border-rule bg-card text-ink-soft hover:border-rule-strong hover:text-ink'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function ChipGroup<T extends string>({
  options,
  values,
  onToggle,
  label,
}: {
  options: ReadonlyArray<{ id: T; label: string }>;
  values: readonly T[];
  onToggle: (value: T) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((option) => {
        const selected = values.includes(option.id);
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onToggle(option.id)}
            className={`rounded-full border px-3.5 py-1.5 font-mono text-xs tracking-wide transition-colors ${
              selected
                ? 'border-ink bg-ink text-paper'
                : 'border-rule bg-card text-ink-soft hover:border-rule-strong hover:text-ink'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

// Optional measurements: an empty box means "I don't know", which the schema
// stores as null. Never coerce a blank to 0 — that reads as a real answer.
export function NumberBox({
  value,
  onChange,
  label,
  suffix,
  prefix,
  step,
  placeholder,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  label: string;
  suffix?: string;
  prefix?: string;
  step?: string;
  placeholder?: string;
}) {
  return (
    <label className="flex items-center gap-2">
      <span className="kicker w-16 shrink-0">{label}</span>
      <span className="flex items-center gap-1 rounded-xs border border-rule bg-card px-2.5 py-1.5 focus-within:border-ink">
        {prefix && <span className="font-mono text-xs text-ink-faint">{prefix}</span>}
        <input
          type="number"
          inputMode="decimal"
          step={step}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className="w-16 bg-transparent font-mono text-sm tabular outline-none"
        />
        {suffix && <span className="font-mono text-xs text-ink-faint">{suffix}</span>}
      </span>
    </label>
  );
}

export function TextBox({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="sr-only">{label}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xs border border-rule bg-card px-3 py-2 text-sm outline-none placeholder:text-ink-faint focus:border-ink"
      />
    </label>
  );
}

export function SelectBox<T extends string | number>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ id: T; label: string }>;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2">
      <span className="kicker shrink-0">{label}</span>
      <select
        value={String(value)}
        onChange={(e) => {
          const match = options.find((o) => String(o.id) === e.target.value);
          if (match) onChange(match.id);
        }}
        className="rounded-xs border border-rule bg-card px-2.5 py-1.5 text-sm outline-none focus:border-ink"
      >
        {options.map((option) => (
          <option key={String(option.id)} value={String(option.id)}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
